import { useReactiveVar } from '@apollo/client'
import {
  Dialog,
  DialogVariant,
  textStyleUtils
} from '@focaldata/cin-ui-components'
import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useState
} from 'react'
import { useAppDispatch, useAppSelector } from '../../../App.store'
import { questionBeingEditedId } from '../../../apollo/apolloClient'
import { EntryType } from '../../../data/gql-gen/questionnaire/graphql'
import {
  DraftQuestionItem,
  QuestionSettingCode,
  SettingValue
} from '../../../data/model/questionnaire'
import useCopyPasteComplete from '../../../hooks/copyPaste/useCopyPasteComplete'
import { useDuplicateQuestionnaireEntry } from '../../../hooks/questionnaire/useDuplicateQuestionnaireEntry'
import useGetDraftQuestionnaire from '../../../hooks/questionnaire/useGetDraftQuestionnaire'
import useQuestionnaireValidation from '../../../hooks/questionnaire/useQuestionnaireValidation'
import useSetQuestionnaireSetting from '../../../hooks/questionnaire/useSetQuestionnaireSetting'
import BasicQuestionLayout from '../../../layouts/BasicQuestionLayout'
import { duplicateQuestionTransactionDatadog } from '../../../tracking/perf/transactions'
import {
  WithEntryOfType,
  getQuestionSettings,
  newEntryId
} from '../../../utils/questionnaireUtils'
import { CardTitleContainer } from '../CardTitle'
import { useHasEarlierWaves } from '../Kpi/HasEarlierWavesContext'
import { describeRemovedKpis, kpisOf, netKpisOf } from '../Kpi/kpiImpact'
import DefaultOptions from '../QuestionCardDefaultOptions/DefaultOptions'
import QuestionCardSwitches from '../QuestionCardSwitches'
import {
  selectSettingsByQuestionId,
  setQuestionSetting
} from '../Questionnaire.slice'
import { flattenEntries } from '../Questionnaire.utils'
import { SurveyQuestionCardHeaderContainer } from '../SurveyQuestionCardHeader'
import Footer from './QuestionCardFooter/QuestionCardFooter.container'
import { ResponseOptionsList } from './ResponseOptions'

interface Props extends WithEntryOfType<EntryType.QuestionEntryType> {
  shouldTitleInputFocus: boolean
}

export const BasicEntryContext = createContext<DraftQuestionItem | undefined>(
  undefined
)

export const useBasicEntryContext = () => {
  const entryItem = useContext(BasicEntryContext)

  if (!entryItem) {
    throw new Error('Missing BasicEntryContext.Provider')
  }

  return entryItem
}

const BasicQuestion: React.FC<Props> = (props: Props) => {
  const { entry, shouldTitleInputFocus } = props
  const { validateBasicQuestionText } = useQuestionnaireValidation()
  const { draftQuestionnaireEntries } = useGetDraftQuestionnaire()
  const { replaceSettings } = useSetQuestionnaireSetting(entry)
  const dispatch = useAppDispatch()
  const hasEarlierWaves = useHasEarlierWaves()
  const { classes: textClasses, cx: classNames } =
    textStyleUtils.useTextStyles()
  const [isMultiSelectConfirmOpen, setIsMultiSelectConfirmOpen] =
    useState(false)

  const entryItem = entry.entryItem
  const { questionLk } = entryItem
  const settingValues =
    // @todo Legacy eslint violation – fix this when editing
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    useAppSelector((state) => selectSettingsByQuestionId(state, questionLk)) ||
    []
  const { pasteToTitle } = useCopyPasteComplete(questionLk)

  const flatDraftQuestionnaireEntries = flattenEntries(
    draftQuestionnaireEntries
  )

  const { duplicateQuestionnaireEntry } = useDuplicateQuestionnaireEntry(
    entry.id
  )

  const handleDuplicateQuestion: () => void = useCallback(() => {
    duplicateQuestionTransactionDatadog.start()
    duplicateQuestionnaireEntry({
      position: entry.position + 1
    })
  }, [duplicateQuestionnaireEntry, entry.position])

  const handleQuestionTypeChange = useCallback(
    async (settingValue: SettingValue) => {
      let newSettings = entry.entryItem.settingValues
        .map((s) => ({
          settingCode: s.code as QuestionSettingCode,
          settingValue: s.value
        }))
        .filter((s) => s.settingCode !== QuestionSettingCode.BasicChoice)
        .concat({ settingCode: QuestionSettingCode.BasicChoice, settingValue })
      dispatch(
        setQuestionSetting({
          questionLk,
          code: QuestionSettingCode.BasicChoice,
          value: settingValue
        })
      )
      switch (settingValue) {
        case SettingValue.MultipleChoice:
          dispatch(
            setQuestionSetting({
              questionLk,
              code: QuestionSettingCode.FlipOptions,
              value: SettingValue.Disabled
            })
          )
          newSettings = newSettings
            .filter((s) => s.settingCode !== QuestionSettingCode.FlipOptions)
            .concat({
              settingCode: QuestionSettingCode.FlipOptions,
              settingValue: SettingValue.Disabled
            })
          break
        case SettingValue.SingleChoice:
          newSettings = newSettings.filter(
            (s) => s.settingCode !== QuestionSettingCode.AutoSumTarget
          )
          break
      }
      await replaceSettings(newSettings)
    },
    [dispatch, questionLk, replaceSettings, entry.entryItem.settingValues]
  )

  const netKpis = netKpisOf(entry)
  const hasNetKpi = netKpis.length > 0
  const handleQuestionTypeSelect = useCallback(
    (settingValue: SettingValue) => {
      if (settingValue === SettingValue.MultipleChoice && hasNetKpi) {
        setIsMultiSelectConfirmOpen(true)
        return
      }
      handleQuestionTypeChange(settingValue)
    },
    [hasNetKpi, handleQuestionTypeChange]
  )

  const closeMultiSelectConfirm = () => setIsMultiSelectConfirmOpen(false)

  const confirmMultiSelect = () => {
    setIsMultiSelectConfirmOpen(false)
    handleQuestionTypeChange(SettingValue.MultipleChoice)
  }

  const handlePasteInTitle = async (
    text: string,
    styledText: string,
    responseOptions: string[]
  ) => {
    questionBeingEditedId(entry.id)
    await pasteToTitle(text, styledText, responseOptions)
  }

  const questionSettings = getQuestionSettings(settingValues)

  const isMultipleChoice =
    questionSettings.get(QuestionSettingCode.BasicChoice) ===
    SettingValue.MultipleChoice

  // @todo Legacy eslint violation – fix this when editing
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const hasEmptyQuestionTitle = entryItem.question === null
  const isBasicQuestionTextEmpty = validateBasicQuestionText(questionLk)
  const newlyCreatedEntryId = useReactiveVar(newEntryId)
  const isNewlyCreatedEntry =
    !!newlyCreatedEntryId && newlyCreatedEntryId === questionLk

  return (
    <BasicEntryContext.Provider value={entryItem}>
      <BasicQuestionLayout
        entryId={entry.id}
        questionCardHeader={
          <SurveyQuestionCardHeaderContainer
            entry={entry}
            hasError={!isNewlyCreatedEntry && isBasicQuestionTextEmpty}
            titleContent={
              <CardTitleContainer
                ariaLabel="Basic question header"
                // for basic question when multilined pasting occurs only first line will be pasted, the rest will be handled by "smart pasting"
                shouldPasteOneLine
                questionLk={questionLk}
                hasError={!isNewlyCreatedEntry && isBasicQuestionTextEmpty}
                shouldTitleInputFocus={shouldTitleInputFocus}
                onPasteInTitle={handlePasteInTitle}
              />
            }
            onQuestionTypeChange={handleQuestionTypeSelect}
            onClickDuplicateIcon={handleDuplicateQuestion}
            isMultipleChoice={isMultipleChoice}
            disabledDuplicate={hasEmptyQuestionTitle}
            entries={flatDraftQuestionnaireEntries}
          />
        }
        switches={
          <QuestionCardSwitches
            entry={entry}
            randomiseQuestionSettingCode={QuestionSettingCode.RandomiseOptions}
            flipOrderQuestionSettingCode={QuestionSettingCode.FlipOptions}
            showFileUpload
          />
        }
        responseOptions={
          <ResponseOptionsList
            entries={flatDraftQuestionnaireEntries}
            currentEntry={entry}
          />
        }
        questionCardFooter={<Footer entry={entry} />}
        defaultOptions={
          <DefaultOptions
            entry={entry}
            options={[
              { code: QuestionSettingCode.DontKnow, label: "Don't know" },
              {
                code: QuestionSettingCode.PreferNotToSay,
                label: 'Prefer not to say'
              },
              { code: QuestionSettingCode.NoneOfThese, label: 'None of these' }
            ]}
          />
        }
      />
      <Dialog
        title="Switch to multi select?"
        open={isMultiSelectConfirmOpen}
        onClose={closeMultiSelectConfirm}
        variant={DialogVariant.DoubleButton}
        primaryButtonText="Switch to multi select"
        primaryButtonClick={confirmMultiSelect}
        secondaryButtonText="Cancel"
        secondaryButtonClick={closeMultiSelectConfirm}
      >
        <p
          className={classNames(
            textClasses.default,
            textClasses.weightSemiBold
          )}
        >
          {`A Net KPI needs a single-select question. ${describeRemovedKpis(
            kpisOf(entry),
            netKpis,
            hasEarlierWaves
          )}`}
        </p>
      </Dialog>
    </BasicEntryContext.Provider>
  )
}

export default memo(BasicQuestion)
